import { createDraftSafeSelector, createSlice } from '@reduxjs/toolkit';
import { globalApi } from '../services/globalApi';

import { regionApi, getLocalSelectedOU } from '../services';
import { RootState } from './index';

interface IAccount {
  global: Record<string, any>;
  system: Record<string, any>;
  regions: Record<string, any>;
  accounts: Record<string, any>;
  entitlements: Record<string, any>;
  loginSaml: Record<string, any>;
  currentAccount: Record<string, any>;
  currentAccountPermission: Record<string, any>;
  currentGroupAccount: Record<string, any>;
  redirectAccount: Record<string, any>;
  isRedirectChecked: boolean;
  warnings: Record<string, any>[];
}

const initialState: IAccount = {
  global: {},
  system: {},
  regions: {},
  accounts: {},
  entitlements: {},
  loginSaml: {},
  currentAccount: {},
  currentAccountPermission: {},
  currentGroupAccount: {},
  redirectAccount: {},
  isRedirectChecked: false,
  warnings: []
};

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    updateCurrentAccount: (state, { payload }) => {
      const cAccount = { ...state.currentAccount };
      if (
        payload?.account_id &&
        !(
          payload?.account_id === cAccount?.account_id &&
          payload?.user_id === cAccount?.user_id &&
          payload?.access_from?.user_id === cAccount?.access_from?.user_id
        )
      ) {
        let account = updateCurrentAccountGlobalToken(
          payload,
          state.global.region_token
        );
        state.currentAccount = account;
      }
    },
    updateCurrentGroupAccount: (state, { payload }) => {
      const cAccount = { ...state.currentGroupAccount };
      if (
        payload?.account_id &&
        !(
          payload?.account_id === cAccount?.account_id &&
          payload?.user_id === cAccount?.user_id &&
          payload?.access_from?.user_id === cAccount?.access_from?.user_id
        )
      ) {
        let account = updateCurrentAccountGlobalToken(
          payload,
          state.global.region_token
        );
        state.currentGroupAccount = account;
      }
    },
    updateRedirectAccount: (state, { payload }) => {
      if (payload) {
        const oldAccount = state.redirectAccount;
        if (
          !(
            oldAccount?.account_id === payload?.account_id &&
            oldAccount?.user_id === payload?.user_id &&
            oldAccount?.access_from?.user_id === payload?.access_from?.user_id
          )
        ) {
          state.redirectAccount = payload;
          state.isRedirectChecked = false;
        }
      }
    },
    updateIsRedirectChecked: (state, { payload }) => {
      state.isRedirectChecked = !!payload;
    },
    updateWarnings: (state, { payload }) => {
      if (payload?.length) {
        state.warnings = payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      globalApi.endpoints.userLogin.matchFulfilled,
      (state, { payload }) => {
        state.global = payload;
        let displayName = payload?.email;
        let displayData: Record<string, any> = {
          username: payload?.email
        };
        const sessionData = payload?.session_data || {};
        if (sessionData?.saml_auth_attributes) {
          if (sessionData?.is_external_idp) {
            const idp_name = sessionData?.saml_auth_attributes.find(
              (o: Record<string, any>) => o.name === 'idp_name'
            );
            const idp_user_id = sessionData?.saml_auth_attributes.find(
              (o: Record<string, any>) => o.name === 'idp_user_id'
            );
            displayName =
              idp_name?.value && idp_user_id?.value
                ? `${idp_name?.value} - ${idp_user_id?.value}`
                : payload?.email;
            displayData = {
              'idp name': idp_name?.value,
              'idp user': idp_user_id?.value
            };
          } else if (sessionData?.is_iam_user) {
            const iam_username = sessionData?.saml_auth_attributes.find(
              (o: Record<string, any>) => o.name === 'IAM_username'
            );
            displayName = iam_username?.value
              ? iam_username?.value
              : payload?.email;
            displayData = {
              username: displayName,
              account: payload?.session_data?.IAM_account_name
            };
          }
        }
        state.loginSaml = {
          displayName,
          is_external_idp: sessionData?.is_external_idp,
          is_iam_user: sessionData?.is_iam_user,
          displayData,
          saml_auth_attributes: sessionData?.saml_auth_attributes
        };

        if (payload.group_region?.length) {
          const regions = payload.group_region;
          regions.forEach((obj) => {
            const alias = obj?.region_alias;
            if (alias) {
              state.regions[alias] = {
                ...obj,
                alias,
                id: obj?.region_id,
                name: obj?.region_alias,
                url: obj?.region_url,
                globalToken: payload.region_token,
                isHide: true
              };
            }
          });
        }
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getSystemData.matchFulfilled,
      (state, { payload }) => {
        state.system = payload;
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getRegions.matchFulfilled,
      (state, { payload }) => {
        payload.forEach((obj) => {
          const alias = obj?.alias;
          if (alias) {
            state.regions[alias] = {
              ...obj,
              globalToken: state.global.region_token,
              isHide: false
            };
          }
        });
      }
    );
    builder.addMatcher(
      regionApi.endpoints.regionLogin.matchFulfilled,
      (state, { payload }) => {
        const region: Record<string, any> = payload || {};
        // regions are keyed by alias; the region login response may use
        // `region` instead of `alias`, so fall back to it
        const alias = region?.alias || region?.region;
        if (alias) {
          const mergedRegion = {
            ...state.regions[alias],
            ...region
          };
          state.regions[alias] = mergedRegion;
          const accountIds = Object.keys(state.accounts);
          accountIds.forEach((id) => {
            const account = state.accounts[id];
            const accountRegion = account?.region;
            if (accountRegion?.alias === alias) {
              state.accounts[id].region = { ...accountRegion, ...mergedRegion };
            }
          });
          // keep the currently selected account in sync so the
          // `!region.token` guard does not re-trigger region login
          if (state.currentAccount?.region?.alias === alias) {
            state.currentAccount = {
              ...state.currentAccount,
              region: { ...state.currentAccount.region, ...mergedRegion }
            };
          }
          if (state.currentGroupAccount?.region?.alias === alias) {
            state.currentGroupAccount = {
              ...state.currentGroupAccount,
              region: { ...state.currentGroupAccount.region, ...mergedRegion }
            };
          }
        }
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getUserPermission.matchFulfilled,
      (state, { payload, meta }) => {
        const args = meta?.arg?.originalArgs;

        state.currentAccountPermission = {
          ...payload,
          account_id: args?.account_id ?? payload?.account_id,
          user_id: args?.user_id ?? payload?.user_id,
          account_node_id: args?.account_node_id ?? payload?.account_node_id,
          args
        };
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getEntitlements.matchFulfilled,
      (state, { payload, meta }) => {
        const accountId = meta?.arg?.originalArgs?.account_id || '';
        if (accountId) {
          state.entitlements[accountId] = payload;
        }
      }
    );
    builder.addMatcher(
      globalApi.endpoints.getUserOrgNodes.matchFulfilled,
      (state, { payload, meta }) => {
        const accounts = payload?.accounts || [];
        const memberAccounts =
          payload?.orgs
            ?.map((org: Record<any, any>) =>
              org?.member_accounts.map((obj: Record<string, any>) => ({
                ...obj,
                org_id: org?.org_id,
                access_from: org?.access_from,
                access_from_user_id: org?.access_from?.user_id
              }))
            )
            ?.flat(1) || [];
        // Merge the region from the `member_accounts[]` entry with the same account_id.
        const allAccounts = [...accounts, ...memberAccounts].map(
          (obj: Record<string, any>) => {
            if (obj?.region || !obj?.account_id) {
              return obj;
            }
            const matched = memberAccounts.find(
              (m: Record<string, any>) =>
                `${m?.account_id}` === `${obj?.account_id}` && !!m?.region
            );
            return matched ? { ...obj, region: matched.region } : obj;
          }
        );

        allAccounts.forEach((obj: Record<string, any>) => {
          const accountId = getAccountId(obj);
          if (accountId) {
            const oldVal = state.accounts[accountId] || {};
            const region = getAccountRegion(obj, state.regions);
            state.accounts[accountId] = { ...oldVal, ...obj, region: region };
          }
        });

        const localSelectedOU = getLocalSelectedOU();
        const localLatestAccount = localSelectedOU?.data;
        if (localLatestAccount?.account_id) {
          const oAccount = allAccounts.find(
            (obj: Record<string, string>) =>
              obj.account_id === localLatestAccount?.account_id &&
              obj.user_id === localLatestAccount?.user_id
          );
          if (oAccount) {
            let account = updateCurrentAccountGlobalToken(
              oAccount,
              state.global.region_token
            );
            state.currentAccount = { ...account };
          }
        }

        // fix FC side only account unauth
        if (allAccounts?.length === 1) {
          state.currentAccount = allAccounts?.[0];
        }
      }
    );
  }
});

const updateCurrentAccountGlobalToken = (
  oAccount: Record<string, any>,
  globalToken: string
) => {
  let account = { ...oAccount };
  if (oAccount?.region) {
    // update globalToken if payload not exists
    if (account?.region?.url && !account.region?.globalToken) {
      account.region = {
        ...account.region,
        globalToken: globalToken
      };
    } else if (Array.isArray(account?.region) && account?.region.length > 0) {
      const accountRegions = account?.region.map(
        (region: Record<string, any>, index: number) => {
          if (region?.url && !region.globalToken) {
            return {
              ...region,
              globalToken: globalToken
            };
          } else {
            return region;
          }
        }
      );
      account.region = accountRegions;
    }
  }
  return account;
};

const getAccountId = (obj: Record<string, any>): string => {
  if (obj.account_id && obj.org_id && obj.access_from_user_id) {
    return [obj.account_id, obj.org_id, obj.access_from_user_id].join('-');
  }
  if (obj.account_id && obj.user_id > 0) {
    return [obj.account_id, obj.user_id].join('-');
  }
  return obj.account_id;
};
const getAccountRegion = (account: any, regions: any) => {
  const region =
    account?.region?.alias && regions[account?.region.alias]
      ? { ...account?.region, ...regions[account?.region.alias] }
      : account?.region;

  return region;
};

const { reducer, actions } = accountSlice;
interface AccountQueryProps {
  accounts: Record<string, any>;
  redirect_obj: Record<string, any>;
}
export const findRedirectAccount = ({
  accounts,
  redirect_obj
}: AccountQueryProps) => {
  const { account_id, org_id, node_id, access_from, user_id } = redirect_obj;
  const access_from_user_id = access_from?.user_id;
  if (account_id) {
    if (org_id && access_from_user_id) {
      return accounts?.[[account_id, org_id, access_from_user_id].join('-')];
    } else if (access_from_user_id) {
      const arr = Object.values(accounts).filter((a: Record<string, any>) => {
        return (
          `${a.account_node_id}` === `${node_id}` &&
          `${a.access_from_user_id}` === `${access_from_user_id}`
        );
      });
      return arr?.[0];
    } else if (user_id > 0) {
      return accounts?.[[account_id, user_id].join('-')];
    } else {
      return accounts?.[account_id];
    }
  }
  return undefined;
};
export const {
  updateCurrentAccount,
  updateCurrentGroupAccount,
  updateRedirectAccount,
  updateIsRedirectChecked,
  updateWarnings
} = actions;

export const selectLoginSaml = (state: RootState) => state.account?.loginSaml;
export const selectSystem = (state: RootState) => state.account?.system;
export const selectAccounts = (state: RootState) => state.account?.accounts;
export const selectGlobalData = (state: RootState) => state.account?.global;
export const selectCurrentAccount = (state: RootState) =>
  state.account?.currentAccount;
export const selectCurrentPermission = (state: RootState) =>
  state.account.currentAccountPermission;

// FC test external IDP account user_id could be 0
const isDefined = (value: unknown) =>
  value !== null && value !== undefined && value !== '';

const isSameAccount = (
  account?: Record<string, any>,
  permission?: Record<string, any>
) => {
  if (!account || !permission) {
    return false;
  }

  if (account.account_id !== permission.account_id) {
    return false;
  }

  if (isDefined(account.user_id) && account.user_id !== permission.user_id) {
    return false;
  }

  if (
    isDefined(account.account_node_id) &&
    isDefined(permission.account_node_id) &&
    account.account_node_id !== permission.account_node_id
  ) {
    return false;
  }

  return true;
};

export const selectAccountWithPermission = createDraftSafeSelector(
  [selectCurrentAccount, selectCurrentPermission],
  (currentAccount, currentPermission): Record<string, any> => {
    if (!currentAccount || !currentPermission) {
      return currentAccount;
    }

    if (!isSameAccount(currentAccount, currentPermission)) {
      return currentAccount;
    }

    return {
      ...currentAccount,
      ...currentPermission
    };
  }
);

export const selectCurrentGroupAccount = (state: RootState) =>
  state.account.currentGroupAccount;
export const selectEntitlements = (state: RootState) =>
  state.account.entitlements;

export const selectIsRedirectChecked = (state: RootState) =>
  state.account?.isRedirectChecked;
export const selectOriginalRedirectAccount = (state: RootState) =>
  state.account?.redirectAccount;

export const selectRedirectAccount = createDraftSafeSelector(
  [selectAccounts, selectOriginalRedirectAccount],
  (accounts, rAccount): Record<string, any> => {
    const obj = findRedirectAccount({ accounts, redirect_obj: rAccount });
    return rAccount?.account_id ? (obj ? obj : { account_id: -1 }) : {};
  }
);
export const selectWarnings = (state: RootState) => state.account.warnings;
export default reducer;
